import { camelToKebab } from "@/utils/changeVarsPatten";

const injectedStyles = new Set<string>();
let sharedStyleTag: HTMLStyleElement | null = null;

type StyleEntry = {
	name: string;
	style: React.CSSProperties & {
		[key: string]: string | number | React.CSSProperties;
	};
};

export function resetStyleInjection() {
	sharedStyleTag = null;
	injectedStyles.clear();
}

/**
 * Converte um objeto de estilos React (`styleObj`) em uma classe CSS dinâmica,
 * injeta essa classe no `<head>` do documento e retorna o nome da classe.
 *
 * @param {React.CSSProperties} styleObj - Objeto de estilos no formato camelCase usado pelo React.
 * @param {string} name - Nome da classe CSS a ser gerada.
 * @returns {string} O nome da classe CSS criada e aplicada ao documento.
 *
 * @example
 * const style = { backgroundColor: 'red', fontSize: '16px' };
 * const className = styleToClass(style, 'my-class');
 * // Resultado: injeta ".my-class { background-color: red; font-size: 16px; }" no <head>
 */
export function styleToClass(
	styleObj: React.CSSProperties & { [key: string]: any },
	name: string
): string | null {
	if (typeof document === "undefined") return null;

	if (!sharedStyleTag) {
		sharedStyleTag = document.createElement("style");
		document.head.appendChild(sharedStyleTag);
	}
	const className = `${name}`;

	if (injectedStyles.has(className)) {
		return className;
	}

	let baseCSS = "";
	let mediaCSS = "";

	for (const key in styleObj) {
		const value = styleObj[key];
		if (key.startsWith("@media")) {
			const mediaRules = value as React.CSSProperties;
			const mediaString = Object.entries(mediaRules)
				.map(([prop, val]) => `${camelToKebab(prop)}: ${val};`)
				.join(" ");
			mediaCSS += `${key} { .${className} { ${mediaString} } }\n`;
		} else {
			baseCSS += `${camelToKebab(key)}: ${value}; `;
		}
	}

	sharedStyleTag.innerHTML += `.${name} { ${baseCSS} }\n${mediaCSS}`;

	injectedStyles.add(className);
	return className;
}
